import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import type { GroupChannel } from "@sendbird/chat/groupChannel";
import { type BaseMessage, SendingStatus, type UserMessage } from "@sendbird/chat/message";
import type { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { isWorkplaceGroupChannel } from "@src/appV2/Chat/utils";
import { CbhIcon, type CbhIconProps, Text } from "@src/appV2/redesign/system/ui-components";

const MESSAGE_STATUS = {
  SENDING: "sending",
  SENT: "sent",
  DELIVERED: "delivered",
  SEEN: "seen",
} as const;

type MessageStatus = (typeof MESSAGE_STATUS)[keyof typeof MESSAGE_STATUS];

interface UserCustomChatMessageProps {
  message: UserMessage;
  isMessageByCurrentUser: boolean;
  channel: GroupChannel;
}

function getMessageStatus(message: UserMessage, channel: GroupChannel): MessageStatus | undefined {
  if (message.sendingStatus === SendingStatus.PENDING) {
    return MESSAGE_STATUS.SENDING;
  }

  if (message.sendingStatus === SendingStatus.FAILED) {
    return undefined;
  }

  const unreadCount = channel.getUnreadMemberCount(message);
  if (unreadCount === 0) {
    return MESSAGE_STATUS.SEEN;
  }

  const undeliveredCount = channel.getUndeliveredMemberCount(message);
  if (undeliveredCount === 0) {
    return MESSAGE_STATUS.DELIVERED;
  }

  return MESSAGE_STATUS.SENT;
}

const STATUS_ICON_MAP: Record<MessageStatus, CbhIconProps["type"]> = {
  [MESSAGE_STATUS.SENDING]: "clock",
  [MESSAGE_STATUS.SENT]: "checkmark-double",
  [MESSAGE_STATUS.DELIVERED]: "checkmark-double",
  [MESSAGE_STATUS.SEEN]: "checkmark-double",
};

function isSendableMessage(message: BaseMessage): message is SendableMessageType {
  return message.messageType === "user" || message.messageType === "file";
}

function getParentMessagePreview(parentMessage: BaseMessage): string {
  if (parentMessage.messageType === "user") {
    return (parentMessage as UserMessage).message;
  }

  if (parentMessage.messageType === "file") {
    return "Photo";
  }

  return "";
}

function getParentMessageSenderName(
  parentMessage: BaseMessage,
  isWorkplaceGroupChat: boolean
): string {
  if (!isSendableMessage(parentMessage)) {
    return "";
  }

  const nickname = parentMessage.sender?.nickname;
  if (!isDefined(nickname)) {
    return "";
  }

  return isWorkplaceGroupChat ? nickname.split(" ")[0] : nickname;
}

export function UserCustomChatMessage(props: UserCustomChatMessageProps) {
  const { message, isMessageByCurrentUser, channel } = props;
  const isWorkplaceGroupChat = isWorkplaceGroupChannel(channel.url);

  let borderRadius = "4px 24px 24px 24px"; // default incoming
  if (isMessageByCurrentUser) {
    borderRadius = "24px 24px 4px 24px"; // outgoing
  } else if (isWorkplaceGroupChat) {
    borderRadius = "24px 24px 24px 4px"; // incoming for workplace group chat
  }

  const messageStatus = isMessageByCurrentUser ? getMessageStatus(message, channel) : undefined;
  const isFailed = isMessageByCurrentUser && message.sendingStatus === SendingStatus.FAILED;
  const { parentMessage } = message;
  const hasParentMessage = isDefined(parentMessage);

  return (
    <Stack
      direction="column"
      alignItems={isMessageByCurrentUser ? "flex-end" : "flex-start"}
      gap={1}
    >
      {isFailed && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={(theme) => ({ color: theme.palette.error.main })}
        >
          <CbhIcon type="triangle-exclamation" size="xSmall" />
          <Text variant="body2" sx={{ color: "inherit" }}>
            Not Delivered
          </Text>
        </Stack>
      )}
      <Box
        sx={{
          ...(isMessageByCurrentUser
            ? {
                paddingLeft: 5,
                paddingRight: 2,
                paddingTop: 5,
                paddingBottom: isFailed ? 5 : 2,
              }
            : { padding: 5 }),
          background: (theme) =>
            isMessageByCurrentUser ? theme.palette.info.light : theme.palette.secondary.main,
          borderRadius,
        }}
      >
        <Stack direction="column" gap={0}>
          {hasParentMessage && (
            <Box
              sx={(theme) => ({
                borderLeft: `2px solid ${theme.palette.text.tertiary}`,
                paddingLeft: 2,
                marginBottom: 2,
              })}
            >
              <Text
                semibold
                variant="caption"
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              >
                {getParentMessageSenderName(parentMessage, isWorkplaceGroupChat)}
              </Text>
              <Text
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                })}
              >
                {getParentMessagePreview(parentMessage)}
              </Text>
            </Box>
          )}
          <Text
            variant="body1"
            color="text.primary"
            sx={{ paddingRight: isMessageByCurrentUser ? 3 : 0 }}
          >
            {message.message}
          </Text>
          {messageStatus && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              sx={(theme) => ({
                color:
                  messageStatus === MESSAGE_STATUS.SEEN
                    ? theme.palette.text.primary
                    : theme.palette.text.tertiary,
              })}
            >
              <CbhIcon type={STATUS_ICON_MAP[messageStatus]} size="small" />
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
